import type { Metric } from "@unbox/api";
import { useCallback, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { AVAILABLE_METRICS } from "@/constants/metrics";
import type { FilterOperator } from "@/providers/filter-registry";

export type ReportLayoutFilter = "all" | "chart" | "table";

export type ReportFilterId = "layout" | "metrics" | "createdDateRange" | "editedDateRange";

export type ReportFiltersState = {
  layout: ReportLayoutFilter;
  metrics: Metric[];
  createdDateRange: { start: string; end: string } | null;
  editedDateRange: { start: string; end: string } | null;
  negated: ReportFilterId[];
};

const defaultFilters: ReportFiltersState = {
  layout: "all",
  metrics: [],
  createdDateRange: null,
  editedDateRange: null,
  negated: [],
};

export const useReportFilters = (
  filters: ReportFiltersState,
  onFiltersChange: (filters: ReportFiltersState) => void,
) => {
  const [metricsSearch, setMetricsSearch] = useState("");

  const form = useForm<ReportFiltersState>({
    defaultValues: filters,
    values: filters,
  });

  // Watch form values and sync with parent
  const watchedValues = useWatch({ control: form.control });

  // Sync form changes to parent
  useMemo(() => {
    if (watchedValues && JSON.stringify(watchedValues) !== JSON.stringify(filters)) {
      onFiltersChange(watchedValues as ReportFiltersState);
    }
  }, [watchedValues, filters, onFiltersChange]);

  const reset = useCallback(() => {
    form.reset(defaultFilters);
    onFiltersChange(defaultFilters);
  }, [form, onFiltersChange]);

  // Filter metrics options based on search
  const filteredMetricsOptions = useMemo(() => {
    if (!metricsSearch.trim()) {
      return AVAILABLE_METRICS.map((m) => ({ id: m.value, label: m.label }));
    }
    const searchLower = metricsSearch.toLowerCase();
    return AVAILABLE_METRICS.filter(
      (m) => m.label.toLowerCase().includes(searchLower) || m.description.toLowerCase().includes(searchLower),
    ).map((m) => ({ id: m.value, label: m.label }));
  }, [metricsSearch]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.layout !== "all") count++;
    if (filters.metrics.length > 0) count++;
    if (filters.createdDateRange) count++;
    if (filters.editedDateRange) count++;
    return count;
  }, [filters]);

  // Check if any filter is active
  const hasActiveFilters = activeFiltersCount > 0;

  return {
    form,
    reset,
    metricsSearch,
    setMetricsSearch,
    filteredMetricsOptions,
    activeFiltersCount,
    hasActiveFilters,
  };
};

// Utility function to get initial filters state
export const getDefaultReportFilters = (): ReportFiltersState => ({
  ...defaultFilters,
});

export const applyReportOperator = (
  filters: ReportFiltersState,
  id: ReportFilterId,
  isActive: boolean,
  matches: boolean,
) => (isActive && filters.negated.includes(id) ? !matches : matches);

export const setReportOperator = (
  filters: ReportFiltersState,
  id: ReportFilterId,
  operator: FilterOperator,
): ReportFiltersState => ({
  ...filters,
  negated:
    operator === "is_not" ? [...filters.negated.filter((n) => n !== id), id] : filters.negated.filter((n) => n !== id),
});

export const clearReportFilter = (
  filters: ReportFiltersState,
  id: ReportFilterId,
  cleared: Partial<ReportFiltersState>,
): ReportFiltersState => ({
  ...filters,
  ...cleared,
  negated: filters.negated.filter((n) => n !== id),
});
